import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';
import type { WaveFileType } from '../types';
import { LASFileDetector } from './utils/LASFileDetector';

interface LASConfig {
  fileType: WaveFileType;
  version: string;
  delimiter: string;
  nullValue: string;
}

interface LASSection {
  name: string;
  lines: string[];
}

interface LASCurveDefinition {
  mnemonic: string;
  unit: string;
  description: string;
}

/**
 * Parser for LAS (Log ASCII Standard) files
 * Handles both LAS 2.0 and 3.0 formats for well logging data
 */
export class LASParser {
  
  /**
   * Check if file content can be parsed by this parser
   */
  static canParse(fileContent: string): boolean {
    return LASFileDetector.isLASFile(fileContent);
  }

  /**
   * Parse LAS file content into standardized format
   */
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n').filter(line => line.trim());
    
    if (lines.length < 10) {
      throw new Error('Invalid LAS file: insufficient data');
    }

    // Validate LAS format
    if (!LASFileDetector.isLASFile(fileContent)) {
      throw new Error('Invalid LAS file: missing required sections or invalid format');
    }

    // Parse file structure into sections
    const sections = this.parseSections(lines);
    
    // Extract configuration from VERSION section
    const config = this.extractConfig(sections);
    
    // Extract metadata from WELL section
    const metadata = this.extractMetadata(sections);
    
    // Extract curve definitions from CURVE section
    const curveDefinitions = this.extractCurveDefinitions(sections);
    
    // Parse ASCII data section
    const data = this.parseDataSection(sections, config);
    
    // Generate curves from data
    const curves = this.generateCurves(data, curveDefinitions, config);

    return {
      toolType: 'LAS',
      fileType: 'LAS_DRILLING_DEFAULT',
      curves,
      metadata,
      columnInfo: this.buildColumnInfo(curveDefinitions)
    };
  }

  /**
   * Parse file content into named sections
   */
  private static parseSections(lines: string[]): LASSection[] {
    const sections: LASSection[] = [];
    let currentSection: LASSection | null = null;

    for (const line of lines) {
      const trimmed = line.trim();
      
      // Skip empty lines and comments
      if (!trimmed || trimmed.startsWith('#')) {
        continue;
      }
      
      // Check for section header
      if (trimmed.startsWith('~')) {
        // Save previous section
        if (currentSection) {
          sections.push(currentSection);
        }
        
        // Start new section
        const sectionMatch = trimmed.match(/^~([A-Z_\s]+)/i);
        const sectionName = sectionMatch ? sectionMatch[1].trim() : 'UNKNOWN';
        
        currentSection = {
          name: sectionName,
          lines: []
        };
      } else if (currentSection) {
        // Add line to current section
        currentSection.lines.push(trimmed);
      }
    }
    
    // Don't forget the last section
    if (currentSection) {
      sections.push(currentSection);
    }

    return sections;
  }

  /**
   * Extract configuration from VERSION section
   */
  private static extractConfig(sections: LASSection[]): LASConfig {
    const versionSection = sections.find(s => 
      s.name.toLowerCase().includes('version') || s.name.toLowerCase() === 'v'
    );

    if (!versionSection) {
      throw new Error('Missing VERSION section in LAS file');
    }

    let version = '2.0'; // Default
    let delimiter = ' '; // Default

    for (const line of versionSection.lines) {
      // Extract version (handle spaces around dot)
      if (line.match(/^\s*VERS\s*\.\s*/i)) {
        const versionMatch = line.match(/VERS\s*\.\s*([0-9.]+)/i);
        if (versionMatch) {
          version = versionMatch[1];
        }
      }
      
      // Extract delimiter (handle spaces around dot)
      if (line.match(/^\s*DLM\s*\.\s*/i)) {
        const dlmMatch = line.match(/DLM\s*\.\s*([A-Z]+)/i);
        if (dlmMatch) {
          const dlm = dlmMatch[1].toUpperCase();
          switch (dlm) {
            case 'TAB': delimiter = '\t'; break;
            case 'COMMA': delimiter = ','; break;
            case 'SPACE': delimiter = ' '; break;
            default: delimiter = ' ';
          }
        }
      }
    }

    return {
      fileType: 'LAS_DRILLING_DEFAULT',
      version,
      delimiter,
      nullValue: '-999.25' // Default, will be overridden by WELL section if present
    };
  }

  /**
   * Extract metadata from WELL section
   */
  private static extractMetadata(sections: LASSection[]): Record<string, string> {
    const wellSection = sections.find(s => 
      s.name.toLowerCase().includes('well') || s.name.toLowerCase() === 'w'
    );

    const metadata: Record<string, string> = {};

    if (!wellSection) {
      return metadata;
    }

    for (const line of wellSection.lines) {
      // Parse parameter lines: MNEM.UNIT VALUE : DESCRIPTION
      // Handle case where unit might be empty
      const match = line.match(/^\s*([A-Z]+)\s*\.\s*(\S*)\s*(.+?)\s*:\s*(.*)$/i);
      if (match) {
        let [, mnemonic, unit, value, description] = match;
        
        // If unit looks like part of the value (contains lowercase or numbers), 
        // it's probably actually part of the value
        if (unit && /[a-z0-9]/i.test(unit) && !['SEC', 'ft', 'psi', 'RPM', 'deg', 'API', 'hr', 'klb', 'SPM', 'galUS/min', 'ft/hr', 'A', 'strokes', 'deg/100ft', '°F'].includes(unit)) {
          // Unit is actually part of value
          value = unit + ' ' + value;
          unit = '';
        }
        
        // Store with descriptive names
        const key = this.getMnemonicDisplayName(mnemonic.trim());
        metadata[key] = value.trim();
        
        if (unit.trim()) {
          metadata[key + ' Unit'] = unit.trim();
        }
        
        if (description.trim()) {
          metadata[key + ' Description'] = description.trim();
        }
      }
    }

    return metadata;
  }

  /**
   * Convert LAS mnemonics to display names
   */
  private static getMnemonicDisplayName(mnemonic: string): string {
    const mnemonicMap: Record<string, string> = {
      'STRT': 'Start Depth/Time',
      'STOP': 'Stop Depth/Time', 
      'STEP': 'Step Interval',
      'NULL': 'Null Value',
      'COMP': 'Company',
      'WELL': 'Well Name',
      'FLD': 'Field',
      'LOC': 'Location',
      'SRVC': 'Service Company',
      'DATE': 'Log Date',
      'UWI': 'Unique Well ID',
      'API': 'API Number'
    };

    return mnemonicMap[mnemonic.toUpperCase()] || mnemonic;
  }

  /**
   * Extract curve definitions from CURVE section
   */
  private static extractCurveDefinitions(sections: LASSection[]): LASCurveDefinition[] {
    const curveSection = sections.find(s => 
      s.name.toLowerCase().includes('curve') || s.name.toLowerCase() === 'c'
    );

    if (!curveSection) {
      throw new Error('Missing CURVE section in LAS file');
    }

    const definitions: LASCurveDefinition[] = [];

    for (const line of curveSection.lines) {
      // Parse curve definition: MNEM.UNIT : DESCRIPTION
      // Handle spaces, hyphens, underscores, and various unit formats including special characters and numbers
      const match = line.match(/^([A-Z0-9_\s\-·°/]+?)\s*\.\s*([A-Z0-9_°\/·]*)\s*:\s*(.*)$/i);
      if (match) {
        const [, mnemonic, unit, description] = match;
        definitions.push({
          mnemonic: mnemonic.trim(),
          unit: unit.trim(),
          description: description.trim()
        });
      }
    }

    return definitions;
  }

  /**
   * Parse ASCII data section
   */
  private static parseDataSection(sections: LASSection[], config: LASConfig): Record<string, string>[] {
    const dataSection = sections.find(s => 
      s.name.toLowerCase().includes('ascii') || 
      s.name.toLowerCase() === 'a' ||
      s.name.toLowerCase().includes('data')
    );

    if (!dataSection) {
      throw new Error('Missing ASCII data section in LAS file');
    }

    // First, we need to get the curve definitions to create headers
    const curveSection = sections.find(s => 
      s.name.toLowerCase().includes('curve') || s.name.toLowerCase() === 'c'
    );
    
    if (!curveSection) {
      throw new Error('Missing CURVE section needed for data parsing');
    }

    // Extract mnemonics as headers
    const headers: string[] = [];
    for (const line of curveSection.lines) {
      // More flexible regex to handle spaces, hyphens, underscores, and special characters
      const match = line.match(/^([A-Z0-9_\s\-·°/]+?)\s*\./i);
      if (match) {
        headers.push(match[1].trim());
      }
    }

    // Join all data lines and parse with Papa Parse
    const csvContent = dataSection.lines.join('\n');
    
    // Parse with Papa Parse
    const parseResult = Papa.parse(csvContent, {
      delimiter: config.delimiter,
      skipEmptyLines: true,
      dynamicTyping: false // Keep as strings for now
    });

    if (parseResult.errors.length > 0) {
      console.warn('CSV parsing warnings:', parseResult.errors.slice(0, 3), `(${parseResult.errors.length} total)`);
    }

    const data = parseResult.data as string[][];
    
    // Convert to objects using headers
    const records: Record<string, string>[] = [];
    for (const row of data) {
      if (row.length === headers.length) {
        const record: Record<string, string> = {};
        for (let i = 0; i < headers.length; i++) {
          record[headers[i]] = row[i];
        }
        records.push(record);
      }
    }
    return records;
  }

  /**
   * Generate curves from parsed data
   */
  private static generateCurves(
    data: Record<string, string>[], 
    curveDefinitions: LASCurveDefinition[],
    config: LASConfig
  ): Curve[] {
    if (data.length === 0) {
      throw new Error('No data rows found in LAS file');
    }

    const curves: Curve[] = [];
    
    // Find DATE and TIME columns for timestamp generation
    const dateColumn = curveDefinitions.find(def => 
      def.mnemonic.trim().toLowerCase() === 'date'
    )?.mnemonic;
    
    const timeColumn = curveDefinitions.find(def => 
      def.mnemonic.trim().toLowerCase() === 'time'
    )?.mnemonic;

    if (!dateColumn || !timeColumn) {
      throw new Error('LAS file must have DATE and TIME columns for time-series data');
    }

    // Generate timestamps
    const timestamps: Date[] = [];
    for (const row of data) {
      const dateStr = row[dateColumn];
      const timeStr = row[timeColumn];
      
      if (dateStr && timeStr) {
        const timestamp = this.parseTimestamp(dateStr, timeStr);
        timestamps.push(timestamp);
      }
    }

    // Generate curves for each non-time column
    for (const curveDef of curveDefinitions) {
      // Skip DATE and TIME columns as they're used for x-axis
      if (curveDef.mnemonic.trim().toLowerCase() === 'date' || 
          curveDef.mnemonic.trim().toLowerCase() === 'time') {
        continue;
      }

      const yValues: number[] = [];
      
      for (const row of data) {
        const value = row[curveDef.mnemonic];
        const numValue = this.parseNumericValue(value, config.nullValue);
        yValues.push(numValue);
      }

      curves.push({
        name: curveDef.description || curveDef.mnemonic,
        xValues: [...timestamps], // Copy array
        yValues,
        yUnit: curveDef.unit
      });
    }

    return curves;
  }

  /**
   * Parse timestamp from date and time strings
   */
  private static parseTimestamp(dateStr: string, timeStr: string): Date {
    // Handle MM-DD-YYYY format
    const dateMatch = dateStr.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (!dateMatch) {
      throw new Error(`Invalid date format: ${dateStr}`);
    }

    const [, month, day, year] = dateMatch;
    
    // Handle HH:MM:SS format
    const timeMatch = timeStr.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (!timeMatch) {
      throw new Error(`Invalid time format: ${timeStr}`);
    }

    const [, hours, minutes, seconds] = timeMatch;
    
    return new Date(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds)
    );
  }

  /**
   * Parse numeric value handling null values
   */
  private static parseNumericValue(value: string, nullValue: string): number {
    if (!value || value.trim() === nullValue) {
      return NaN; // Use NaN for null values
    }

    const numValue = parseFloat(value.trim());
    return isNaN(numValue) ? NaN : numValue;
  }

  /**
   * Build column info for metadata
   */
  private static buildColumnInfo(curveDefinitions: LASCurveDefinition[]): Record<string, { unit: string; description?: string }> {
    const columnInfo: Record<string, { unit: string; description?: string }> = {};
    
    for (const curveDef of curveDefinitions) {
      columnInfo[curveDef.mnemonic] = {
        unit: curveDef.unit,
        description: curveDef.description
      };
    }

    return columnInfo;
  }
}
